/** Wire token: `@Label<user:id>`, `@Label<user:id|IANA/Tz>`, or `@Label<group:id>`. */
export const MENTION_WIRE_TOKEN_RE =
  /@([^<\n]+?)<(user|group):([^>|]+)(?:\|([^>]+))?>/g;

export interface MentionSuggestion {
  id: string;
  type: 'user' | 'group';
  label: string;
  secondary?: string;
  timezone?: string;
}

export type ComposerPart =
  | { kind: 'text', text: string }
  | {
      kind: 'mention';
      label: string;
      entityType: 'user' | 'group';
      id: string;
      timezone?: string;
    };

export type UserMessageDisplayPart =
  | { kind: 'text', text: string }
  | { kind: 'mention', label: string, entityType: 'user' | 'group' };

export interface AgentGatewayMentionSelection {
  selectedUsers: Array<{ id: string, name?: string, timezone?: string }>;
  selectedGroups: Array<{ id: string, name?: string }>;
}

export interface ParsedMentionWire {
  /** Human-readable text for chat bubbles (`@Jorge P` without wire ids). */
  displayText: string;
  /** Message for td-agent-gateway (`@[Jorge P](user:id)` tokens). */
  gatewayMessage: string;
  selection: AgentGatewayMentionSelection;
}

/** Company-wide tags; mentioning them is redundant with default report scope. */
export function isRedundantMentionGroup(group: { name?: string }): boolean {
  const normalizedGroupName = (group.name || '').trim().toLowerCase();
  return /^all (regular )?users?$/.test(normalizedGroupName);
}

export interface PlainDisplayMention {
  /** Start index of `@` in the source string. */
  index: number;
  /** Matched text including `@`, e.g. `@Jorge P`. */
  raw: string;
  /** Display label without `@`, e.g. `Jorge P`. */
  label: string;
}

export type ResolveLabelResult =
  | { status: 'resolved', suggestion: MentionSuggestion }
  | { status: 'ambiguous', candidates: MentionSuggestion[] }
  | { status: 'unresolved' };

export interface AmbiguousPlainMention {
  label: string;
  index: number;
  candidates: MentionSuggestion[];
}

export interface ResolvePlainMentionsResult {
  text: string;
  unresolved: PlainDisplayMention[];
  ambiguous: AmbiguousPlainMention[];
}

const GATEWAY_MARKDOWN_MENTION_RE =
  /@\[([^\]]+)\]\((user|group|tag|team):([^)|]+)(?:\|([^)]+))?\)/gi;

/**
 * Words that usually follow a person/group name in freeform Ask AI prompts.
 * Used so `@Jorge P last week` captures `Jorge P`, not `Jorge P last week`.
 */
const MENTION_TRAILING_STOP_RE =
  /^(?:last|this|next|past|previous|today|yesterday|tomorrow|week|weeks|month|months|day|days|year|years|hour|hours|for|in|on|during|between|from|to|with|about|vs\.?|versus|and|or|idle|time|times|hours?|productivity|activity|report|reports|summary|compare|show|what|how|when|where|who)$/i;

/** Convert gateway `@[Label](user:id)` tokens into Ask AI wire format. */
export function normalizeGatewayMarkdownMentions(text: string): string {
  return text.replace(
    GATEWAY_MARKDOWN_MENTION_RE,
    (_full, label: string, kind: string, id: string, tz?: string) => {
      const entityType =
        kind.toLowerCase() === 'user' ? 'user' : 'group';
      const payload =
        entityType === 'user' && tz?.trim() ? `${id}|${tz.trim()}` : id;
      return `@${String(label).trim()}<${entityType}:${payload}>`;
    },
  );
}

/** Ranges already occupied by wire or gateway markdown mention tokens. */
function occupiedMentionRanges(text: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (const re of [
    new RegExp(MENTION_WIRE_TOKEN_RE.source, 'g'),
    new RegExp(GATEWAY_MARKDOWN_MENTION_RE.source, 'g'),
  ]) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      ranges.push([m.index, re.lastIndex]);
    }
  }
  return ranges;
}

function isInsideRange(index: number, ranges: Array<[number, number]>): boolean {
  return ranges.some(([start, end]) => index >= start && index < end);
}

function trimTrailingStopWords(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  while (words.length > 1 && MENTION_TRAILING_STOP_RE.test(words[words.length - 1])) {
    words.pop();
  }
  return words.join(' ');
}

/**
 * Find plain `@Label` tokens pasted or typed without wire ids — e.g.
 * `compare idle between @Jorge P + @Engineering Dept`.
 */
export function findPlainDisplayMentions(text: string): PlainDisplayMention[] {
  const occupied = occupiedMentionRanges(text);
  const out: PlainDisplayMention[] = [];
  // Stop at separators, punctuation, or EOL. Trailing stop-words trimmed after match.
  const re =
    /(?:^|[\s>])(@)([A-Za-z][A-Za-z0-9'.\- &]*?)(?=\s*(?:\+|vs\.?|versus|,|\)|\]|[?!:;.]|$)|(?:\s+(?:and|or)\s+)|$)/gi;

  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const atIndex = m.index + (m[0].length - m[1].length - m[2].length);
    if (isInsideRange(atIndex, occupied)) {
      continue;
    }
    const label = trimTrailingStopWords(m[2].trim());
    if (!label) {
      continue;
    }
    out.push({
      index: atIndex,
      raw: `@${label}`,
      label,
    });
  }
  return out;
}

function mentionWireTokenFromSuggestion(
  pick: MentionSuggestion,
): string {
  const tz = pick.timezone?.trim();
  const payload =
    pick.type === 'user' && tz ? `${pick.id}|${tz}` : pick.id;
  return `@${pick.label}<${pick.type}:${payload}>`;
}

/** Replace plain `@Label` tokens with Ask AI wire tokens when resolved. */
export function injectResolvedPlainMentions(
  text: string,
  mentions: PlainDisplayMention[],
  resolvedByIndex: Map<number, MentionSuggestion>,
): string {
  if (!mentions.length || !resolvedByIndex.size) {
    return text;
  }
  let out = text;
  const sorted = [...mentions].sort((a, b) => b.index - a.index);
  for (const mention of sorted) {
    const pick = resolvedByIndex.get(mention.index);
    if (!pick) {
      continue;
    }
    out =
      out.slice(0, mention.index) +
      mentionWireTokenFromSuggestion(pick) +
      out.slice(mention.index + mention.raw.length);
  }
  return out;
}

/**
 * Resolve plain `@Name` tokens to wire ids via an async lookup (users + groups).
 * Shortens over-captured labels word-by-word until a unique match is found.
 * Ambiguous / unresolved mentions are reported so the UI can ask for confirmation.
 */
export async function resolvePlainMentionsInText(
  text: string,
  resolveLabel: (label: string) => Promise<ResolveLabelResult>,
): Promise<ResolvePlainMentionsResult> {
  const normalized = normalizeGatewayMarkdownMentions(text);
  const plain = findPlainDisplayMentions(normalized);
  if (!plain.length) {
    return { text: normalized, unresolved: [], ambiguous: [] };
  }

  const resolvedByIndex = new Map<number, MentionSuggestion>();
  const resolvedMentions: PlainDisplayMention[] = [];
  const unresolved: PlainDisplayMention[] = [];
  const ambiguous: AmbiguousPlainMention[] = [];

  for (const mention of plain) {
    const outcome = await resolveLabelWithBackoff(mention.label, resolveLabel);
    if (outcome.status === 'resolved') {
      const matchedLabel = outcome.matchedLabel;
      resolvedMentions.push({
        index: mention.index,
        label: matchedLabel,
        raw: `@${matchedLabel}`,
      });
      resolvedByIndex.set(mention.index, outcome.suggestion);
      continue;
    }
    if (outcome.status === 'ambiguous') {
      ambiguous.push({
        label: outcome.label,
        index: mention.index,
        candidates: outcome.candidates,
      });
      continue;
    }
    unresolved.push(mention);
  }

  return {
    text: injectResolvedPlainMentions(normalized, resolvedMentions, resolvedByIndex),
    unresolved,
    ambiguous,
  };
}

async function resolveLabelWithBackoff(
  label: string,
  resolveLabel: (label: string) => Promise<ResolveLabelResult>,
): Promise<
  | { status: 'resolved', suggestion: MentionSuggestion, matchedLabel: string }
  | { status: 'ambiguous', label: string, candidates: MentionSuggestion[] }
  | { status: 'unresolved' }
> {
  const words = label.trim().split(/\s+/).filter(Boolean);
  let ambiguous:
    | { status: 'ambiguous', label: string, candidates: MentionSuggestion[] }
    | null = null;

  for (let len = words.length; len >= 1; len--) {
    const candidate = words.slice(0, len).join(' ');
    const result = await resolveLabel(candidate);
    if (result.status === 'resolved') {
      return {
        status: 'resolved',
        suggestion: result.suggestion,
        matchedLabel: candidate,
      };
    }
    if (result.status === 'ambiguous' && !ambiguous) {
      ambiguous = {
        status: 'ambiguous',
        label: candidate,
        candidates: result.candidates,
      };
      // Keep shortening — a shorter unique prefix may still resolve cleanly.
    }
  }

  if (ambiguous) {
    return ambiguous;
  }
  return { status: 'unresolved' };
}

function mentionWireToken(p: Extract<ComposerPart, { kind: 'mention' }>): string {
  const payload =
    p.entityType === 'user' && p.timezone?.trim()
      ? `${p.id}|${p.timezone.trim()}`
      : p.id;
  return `@${p.label}<${p.entityType}:${payload}>`;
}

export function wireFromParts(parts: ComposerPart[]): string {
  return parts
    .map((p) => (p.kind === 'text' ? p.text : mentionWireToken(p)))
    .join('');
}

export function partsFromWire(wire: string): ComposerPart[] {
  const parts: ComposerPart[] = [];
  const re = new RegExp(MENTION_WIRE_TOKEN_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(wire)) !== null) {
    if (m.index > last) {
      parts.push({ kind: 'text', text: wire.slice(last, m.index) });
    }
    const entityType = m[2] as 'user' | 'group';
    const timezone =
      entityType === 'user' && m[4]?.trim() ? m[4].trim() : undefined;
    parts.push({
      kind: 'mention',
      label: m[1].trim(),
      entityType,
      id: m[3].trim(),
      ...(timezone ? { timezone } : {}),
    });
    last = re.lastIndex;
  }
  if (last < wire.length) {
    parts.push({ kind: 'text', text: wire.slice(last) });
  }
  if (parts.length === 0) {
    parts.push({ kind: 'text', text: '' });
  }
  if (parts[0].kind === 'mention') {
    parts.unshift({ kind: 'text', text: '' });
  }
  if (parts[parts.length - 1].kind === 'mention') {
    parts.push({ kind: 'text', text: '' });
  }
  return parts;
}

export function mergeAdjacentTextParts(parts: ComposerPart[]): ComposerPart[] {
  const out: ComposerPart[] = [];
  for (const part of parts) {
    if (part.kind === 'text') {
      if (out.length && out[out.length - 1].kind === 'text') {
        (out[out.length - 1] as { kind: 'text', text: string }).text += part.text;
      } else {
        out.push({ kind: 'text', text: part.text });
      }
    } else {
      out.push({ ...part });
    }
  }
  if (out.length === 0) {
    out.push({ kind: 'text', text: '' });
  }
  return out;
}

export function partWireLen(p: ComposerPart): number {
  return p.kind === 'text' ? p.text.length : mentionWireToken(p).length;
}

/**
 * Find an in-progress @mention at the cursor. Uses the same rule as Ask AI:
 * `@` must follow start-of-string, whitespace, or `>` (end of a prior wire token).
 */
export function findActiveMentionAtCursor(
  wire: string,
  cursor: number,
): { start: number, end: number, query: string } | null {
  const beforeCursor = wire.slice(0, Math.max(0, cursor));
  const match = beforeCursor.match(/(?:^|\s|>)@([^\s@<>]*)$/);
  if (!match) {
    return null;
  }
  const query = (match[1] ?? '').trim();
  const start = (match.index ?? 0) + match[0].indexOf('@');
  return { start, end: cursor, query };
}

export function userMessageSegments(
  content: string | undefined | null,
): UserMessageDisplayPart[] {
  const s = content == null ? '' : String(content);
  if (!s) {
    return [];
  }
  const out: UserMessageDisplayPart[] = [];
  const re = new RegExp(MENTION_WIRE_TOKEN_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) {
      out.push({ kind: 'text', text: s.slice(last, m.index) });
    }
    out.push({
      kind: 'mention',
      label: m[1].trim(),
      entityType: m[2] as 'user' | 'group',
    });
    last = re.lastIndex;
  }
  if (last < s.length) {
    out.push({ kind: 'text', text: s.slice(last) });
  }
  if (out.length === 0) {
    out.push({ kind: 'text', text: s });
  }
  return out;
}

/** Map Ask AI wire format to td-agent-gateway message + structured filters. */
export function parseMentionWire(wire: string): ParsedMentionWire {
  const selectedUsers: AgentGatewayMentionSelection['selectedUsers'] = [];
  const selectedGroups: AgentGatewayMentionSelection['selectedGroups'] = [];
  const userIds = new Set<string>();
  const groupIds = new Set<string>();

  let displayText = '';
  let gatewayMessage = '';
  const re = new RegExp(MENTION_WIRE_TOKEN_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(wire)) !== null) {
    const prefix = wire.slice(last, m.index);
    displayText += prefix;
    gatewayMessage += prefix;

    const label = m[1].trim();
    const entityType = m[2] as 'user' | 'group';
    const id = m[3].trim();
    const timezone =
      entityType === 'user' && m[4]?.trim() ? m[4].trim() : undefined;

    displayText += `@${label}`;
    gatewayMessage += `@[${label}](${entityType}:${id})`;

    if (entityType === 'user' && id && !userIds.has(id)) {
      userIds.add(id);
      selectedUsers.push({
        id,
        name: label,
        ...(timezone ? { timezone } : {}),
      });
    }
    if (entityType === 'group' && id && !groupIds.has(id)) {
      groupIds.add(id);
      selectedGroups.push({ id, name: label });
    }

    last = re.lastIndex;
  }

  const suffix = wire.slice(last);
  displayText += suffix;
  gatewayMessage += suffix;

  return {
    displayText: displayText.trim(),
    gatewayMessage: gatewayMessage.trim(),
    selection: { selectedUsers, selectedGroups },
  };
}
